import React, { useCallback, useEffect, useState } from 'react';
import { auth } from '../../firebase/config';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { getViewerTimezone } from '../../utils/dateTime';
import CmaReviewModal from './CmaReviewModal';
import './CmaPricingCard.css';


const getFirebaseToken = () => new Promise((resolve, reject) => {
  if (auth.currentUser) {
    auth.currentUser.getIdToken(false).then(resolve).catch(reject);
  } else {
    const { onAuthStateChanged } = require('firebase/auth');
    const unsub = onAuthStateChanged(auth, user => {
      unsub();
      if (user) user.getIdToken(false).then(resolve).catch(reject);
      else reject(new Error('Not authenticated'));
    });
    setTimeout(() => reject(new Error('Auth timeout')), 10000);
  }
});

const buildAddress = (transaction) => {
  const parts = [
    transaction?.propertyAddress,
    transaction?.propertyCity,
    transaction?.propertyState,
    transaction?.propertyZip,
  ].filter(Boolean);
  return parts.join(', ');
};

// Mirrors the mobile validator (CmaMilestoneBlock.tsx isValidUsAddress), which
// itself mirrors the backend's CmaAnalysisService.validateUsAddress — street
// segment must start with a house number, at least two comma-separated
// segments, and a US state abbreviation somewhere in the string.
const isValidUsAddress = (raw) => {
  if (!raw) return false;
  const trimmed = raw.trim();
  if (!trimmed) return false;
  const parts = trimmed.split(',');
  if (parts.length < 2) return false;
  const street = parts[0].trim();
  if (!/^\d+\s+\S/.test(street)) return false;
  return /\b(A[LKZR]|C[AOT]|D[EC]|FL|GA|HI|I[DLNA]|K[SY]|LA|M[EDAINSOT]|N[EVHJMYCD]|O[HKR]|PA|RI|S[CD]|T[NX]|UT|V[TA]|W[AVIY])\b/.test(trimmed.toUpperCase());
};

const formatMoney = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (n === undefined || n === null || Number.isNaN(n)) return '—';
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const formatRange = (lo, hi) => {
  if (lo === undefined || lo === null || hi === undefined || hi === null) return '—';
  const abbr = (v) => (v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M` : `$${(v / 1000).toFixed(0)}K`);
  return `${abbr(lo)}-${abbr(hi)}`;
};

const formatReportTimestamp = (iso) => {
  if (!iso) return { day: '—', time: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: '—', time: '' };
  const zone = getViewerTimezone();
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: zone });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: zone });
  return { day, time };
};

const shortAddress = (full) => {
  const first = (full || '').split(',')[0]?.trim();
  return first || full || 'CMA Report';
};

// Fetch the report PDF as an authenticated Blob (the endpoint requires a
// Bearer token, so a plain <a href> / window.open to the raw URL won't work).
const fetchReportPdfBlob = async (reportId) => {
  const token = await getFirebaseToken();
  const response = await apiClient.get(API_ENDPOINTS.cmaReportPdf(reportId), {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob',
  });
  return new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
};

// "CMA & Pricing" milestone — mirrors mobile CmaMilestoneBlock.tsx exactly:
// empty state shows "Generate CMA report"; populated state shows a summary
// card (estimated value/range/comps/DOM/price-per-sqft from the latest
// report) + Regenerate + a Generated Reports list (view/download per report).
// Backend: rialty-analysis-service via /api/v1/cma/** (see config/api.js).
const CmaPricingCard = ({ transaction, isAgent, locked, onGenerated }) => {
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false); // comp review before every generation
  const [busyRow, setBusyRow] = useState(null); // { id, action: 'view' | 'download' }
  const [reports, setReports] = useState([]); // CmaReportSummary[] — newest first
  const [latest, setLatest] = useState(null); // full CmaReportResponse for reports[0]
  const [error, setError] = useState('');

  const address = buildAddress(transaction);

  const loadHistory = useCallback(async () => {
    if (!address) {
      setLoadingHistory(false);
      return;
    }
    try {
      const token = await getFirebaseToken();
      const historyResp = await apiClient.get(API_ENDPOINTS.cmaHistory, {
        headers: { Authorization: `Bearer ${token}` },
        params: { address, transactionId: transaction.id },
      });
      const history = Array.isArray(historyResp.data) ? historyResp.data : [];
      setReports(history);
      if (history.length > 0) {
        try {
          const reportResp = await apiClient.get(API_ENDPOINTS.cmaReportById(history[0].reportId), {
            headers: { Authorization: `Bearer ${token}` },
          });
          setLatest(reportResp.data);
        } catch {
          setLatest(null);
        }
      } else {
        setLatest(null);
      }
    } catch {
      setReports([]);
      setLatest(null);
    } finally {
      setLoadingHistory(false);
    }
  }, [address, transaction.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Every CMA goes through the comp review first (draft -> review -> generate); there is no
  // direct one-click generation from this card any more.
  const handleGenerate = () => {
    if (locked) return;
    if (!address) {
      setError('This transaction has no property address on file. Add one before generating a CMA.');
      return;
    }
    setError('');
    setReviewOpen(true);
  };

  const handleReviewGenerated = async () => {
    await loadHistory();
    onGenerated && onGenerated();
  };

  const reviewModal = reviewOpen && (
    <CmaReviewModal
      transaction={transaction}
      address={address}
      draftId={latest?.draftId}
      onClose={() => setReviewOpen(false)}
      onGenerated={handleReviewGenerated}
    />
  );

  const buildFileName = (report) => {
    const day = formatReportTimestamp(report.createdAt).day || 'Report';
    const fullAddress = (report.address || address || 'CMA').replace(/[/:*?"<>|]/g, '');
    return `${fullAddress} - ${day}.pdf`;
  };

  const handleViewReport = async (report) => {
    // Open the tab synchronously within the click gesture so popup blockers allow it,
    // then navigate it to the blob URL once the download finishes (matches
    // TransactionDocuments.js handleViewInDocuSign).
    const newTab = window.open('', '_blank');
    if (newTab) {
      newTab.document.write(
        '<!DOCTYPE html><html><head><title>Opening report…</title></head>' +
        '<body style="font-family:sans-serif;color:#2D1B69;display:flex;align-items:center;justify-content:center;height:100vh;margin:0">' +
        '<p>Opening report…</p></body></html>'
      );
      newTab.document.close();
    }
    setBusyRow({ id: report.reportId, action: 'view' });
    try {
      const blob = await fetchReportPdfBlob(report.reportId);
      const blobUrl = window.URL.createObjectURL(blob);
      if (newTab) newTab.location.href = blobUrl;
      else window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening CMA report:', err);
      if (newTab) newTab.close();
      alert('The PDF could not be opened. Please try again.');
    } finally {
      setBusyRow(null);
    }
  };

  const handleDownloadReport = async (report) => {
    setBusyRow({ id: report.reportId, action: 'download' });
    try {
      const blob = await fetchReportPdfBlob(report.reportId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildFileName(report);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading CMA report:', err);
      alert('The PDF could not be downloaded. Please try again.');
    } finally {
      setBusyRow(null);
    }
  };

  // ─── Loading ──────────────────────────────────────────────────────────────
  if (loadingHistory) {
    return (
      <div className="txn-cma-section">
        <div className="txn-cma-loading-spinner" />
      </div>
    );
  }

  // ─── Empty state ────────────────────────────────────────────────────────
  if (reports.length === 0) {
    if (!isAgent) return null;
    const addressUsable = isValidUsAddress(address);
    return (
      <div className="txn-cma-section">
        {addressUsable ? (
          <>
            <div className="txn-cma-action-row">
              <button
                className="txn-rpa-launch-btn"
                disabled={locked}
                onClick={handleGenerate}
                title={locked ? 'Complete or mark N/A all previous stages to unlock CMA & Pricing.' : undefined}
              >
                Review comps &amp; generate
              </button>
            </div>
            {error && <div className="txn-cma-error">{error}</div>}
          </>
        ) : (
          <div className="txn-cma-address-hint">
            Set a full US property address on this transaction to generate a CMA. Format: “123 Main St, San Francisco, CA 94105”.
            {address && <div className="txn-cma-address-hint-current">Current: {address}</div>}
          </div>
        )}
        {reviewModal}
      </div>
    );
  }

  // ─── Populated state ──────────────────────────────────────────────────────
  const rec = latest?.pricingRecommendation;
  const periods = latest?.analysisPeriods || [];
  const p90 = periods.find((p) => p.days === 90) || periods[periods.length - 1];

  return (
    <div className="txn-cma-section">
      <div className="txn-cma-summary">
        <div className="txn-cma-summary-grid">
          <div className="txn-cma-summary-item">
            <div className="txn-cma-summary-label">Estimated value</div>
            <div className="txn-cma-summary-value">{formatMoney(rec?.suggestedListPrice)}</div>
          </div>
          <div className="txn-cma-summary-item">
            <div className="txn-cma-summary-label">Value range</div>
            <div className="txn-cma-summary-value">{formatRange(rec?.lowEstimate, rec?.highEstimate)}</div>
          </div>
          <div className="txn-cma-summary-item">
            <div className="txn-cma-summary-label">Comps used</div>
            <div className="txn-cma-summary-value">{p90?.comparableCount ?? 0} properties</div>
          </div>
          <div className="txn-cma-summary-item">
            <div className="txn-cma-summary-label">Avg days on market</div>
            <div className="txn-cma-summary-value">{p90?.averageDaysOnMarket ? `${Math.round(Number(p90.averageDaysOnMarket))} days` : '—'}</div>
          </div>
          <div className="txn-cma-summary-item">
            <div className="txn-cma-summary-label">Price / sq ft</div>
            <div className="txn-cma-summary-value">{p90?.averagePricePerSqFt ? `${formatMoney(p90.averagePricePerSqFt)}/sqft` : '—'}</div>
          </div>
        </div>
      </div>

      {isAgent && (
        <div className="txn-cma-action-row">
          <button
            className="txn-cma-regenerate-btn"
            disabled={locked}
            onClick={handleGenerate}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
            Review comps &amp; regenerate
          </button>
        </div>
      )}

      {error && <div className="txn-cma-error">{error}</div>}

      <div className="txn-cma-report-list">
        <div className="txn-cma-report-list-title">
          Generated reports{reports.length > 1 ? ` (${reports.length})` : ''}
        </div>
        {reports.map((report, idx) => {
          const stamp = formatReportTimestamp(report.createdAt);
          const isBusy = busyRow?.id === report.reportId;
          return (
            <div key={report.reportId} className="txn-cma-report-row">
              <div className="txn-cma-report-info">
                <div className="txn-cma-report-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><rect x="7" y="12" width="3" height="6"/><rect x="12" y="8" width="3" height="10"/><rect x="17" y="5" width="3" height="13"/></svg>
                </div>
                <div>
                  <div className="txn-cma-report-name">
                    {shortAddress(report.address || address)} - {stamp.day}
                    {report.version > 1 && <span className="txn-cma-version-tag">v{report.version}</span>}
                    {idx === 0 && <span className="txn-cma-latest-tag">Latest</span>}
                  </div>
                  <div className="txn-cma-report-sub">Generated {stamp.time}</div>
                </div>
              </div>
              <div className="txn-cma-report-actions">
                <button
                  className="txn-cma-icon-btn"
                  title="View report"
                  aria-label="View report"
                  disabled={isBusy}
                  onClick={() => handleViewReport(report)}
                >
                  {isBusy && busyRow.action === 'view' ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                  ) : (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
                <button
                  className="txn-cma-icon-btn"
                  title="Download PDF"
                  aria-label="Download PDF"
                  disabled={isBusy}
                  onClick={() => handleDownloadReport(report)}
                >
                  {isBusy && busyRow.action === 'download' ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="txn-rpa-spinner-icon"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                  ) : (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {reviewModal}
    </div>
  );
};

export default CmaPricingCard;
